import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { useState, useEffect } from 'react';
import { fetchData, postData } from '../../../services';
import Modal from '../../helpers/Modal';
export const TransferProjectForm = ({ project, onProjectTransferred }) => {
    const { token, base_url, setProjects } = useAppContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');

    // Load companies when modal opens
    useEffect(() => {
        const loadCompanies = async () => {
            if (isModalOpen && token) {
                try {
                    setIsLoadingCompanies(true);
                    const response = await fetchData(token, `${base_url}/apis/admin/companies`);
                    if (response && response.companies) {
                        // Sort companies alphabetically by company_name
                        const sortedCompanies = [...response.companies].sort((a, b) => {
                            const nameA = (a.company_name || '').toLowerCase();
                            const nameB = (b.company_name || '').toLowerCase();
                            return nameA.localeCompare(nameB);
                        });
                        setCompanies(sortedCompanies);
                    }
                } catch (error) {
                    console.error('Error loading companies:', error);
                    window.showAlert(500, 'Failed to load companies');
                } finally {
                    setIsLoadingCompanies(false);
                }
            }
        };

        loadCompanies();
    }, [isModalOpen, token, base_url]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isSubmitting) return; // Prevent multiple submissions
        
        if (!selectedCompanyId) {
            window.showAlert(400, 'Please select a company to transfer the project to');
            return;
        }

        setIsSubmitting(true);

        const data = {
            company_id: selectedCompanyId
        };

        try {
            console.log('Transferring project:', project.id, 'to company:', selectedCompanyId);
            const response = await postData(token, `${base_url}/apis/admin/transfer-ownership/${project.id}`, data);

            if (response && response.project) {
                // Update the projects list in context
                setProjects(prevProjects => 
                    prevProjects.map(p => 
                        p.id === project.id ? response.project : p
                    )
                );

                // Call the callback if provided
                if (onProjectTransferred) {
                    onProjectTransferred(response.project);
                }

                // Show success alert
                window.showAlert(200, 'Project transferred successfully!');
                console.log('Project transferred successfully!');
                
                // Close modal on success
                setIsModalOpen(false);
                setSelectedCompanyId('');
            } else if (response && response.error) {
                window.showAlert(400, response.error);
            } else {
                window.showAlert(500, 'Failed to transfer project. Please try again.');
            }
        } catch (error) {
            console.error('Error transferring project:', error);
            
            // Show error alert based on error type
            if (error.status === 404) {
                window.showAlert(404, 'Project not found');
            } else if (error.status >= 400 && error.status < 500) {
                window.showAlert(error.status, 'Invalid request. Please check your selection.');
            } else {
                window.showAlert(500, 'Failed to transfer project. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        setSelectedCompanyId(e.target.value);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedCompanyId('');
    };

    if (!project) {
        return <div>No project data available</div>;
    }

    const transferProjectForm = (
        <div>
            <h2>Transfer Project</h2>
            <p className="text-secondary-block">
                Transfer this project to a different company. This will change the project ownership.
            </p>
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="company_id" className="form-label">
                        Select Company
                    </label>
                    {isLoadingCompanies ? (
                        <div className="text-secondary-padded">
                            <FontAwesomeIcon icon={faSpinner} spin /> Loading companies...
                        </div>
                    ) : (
                        <select
                            id="company_id"
                            name="company_id"
                            value={selectedCompanyId}
                            onChange={handleChange}
                            className="form-select"
                            required
                        >
                            <option value="">-- Select a company --</option>
                            {companies.map(company => (
                                <option key={company.id} value={company.id}>
                                    {company.company_name}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={handleModalClose}
                        className="btn btn-secondary"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting || isLoadingCompanies || !selectedCompanyId}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin /> Transferring...
                            </>
                        ) : (
                            'Transfer Project'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <div>
            <button className="btn btn-secondary" onClick={() => setIsModalOpen(true)}>
                <FontAwesomeIcon icon={faExchangeAlt} /> Transfer Project
            </button>
            {isModalOpen && (
                <Modal form={transferProjectForm} setIsModalOpen={handleModalClose} />
            )}
        </div>
    )
};

